import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

/**
 * A TOAST SAYS WHAT KIND OF NEWS IT IS BEFORE IT IS READ.
 *
 * Every toast in the app used to be the same small white card: the wrapper set
 * only the NEUTRAL colours (`--normal-bg` and friends), so a success, a
 * warning and a failure were told apart by an icon four pixels wide and by
 * reading the sentence. With 173 error calls and 98 success calls in the app,
 * that is the most common thing on screen wearing the least information.
 *
 * Sonner takes one set of CSS variables PER TYPE, so the colour is declared
 * here once rather than fought over per call site with `classNames`.
 *
 * TINTS, NOT FILLS. `--success` and its siblings are solid colours picked for
 * white text — right for a badge, wrong for a card carrying two lines of
 * prose. Each type mixes its own colour into the card surface for the
 * background, keeps it near-solid for the border so the card has a real edge,
 * and darkens it toward the foreground for the text. `color-mix` means the
 * dark theme follows from its own tokens rather than from a second table.
 *
 * The variables are read from the tokens in `styles.css`, so none of this is a
 * literal palette colour: `--info` was the one status the app had never named,
 * and it is defined there beside the other three.
 */
const tint = (token: string) =>
  ({
    background: `color-mix(in oklab, var(${token}) 12%, var(--popover))`,
    border: `color-mix(in oklab, var(${token}) 42%, transparent)`,
    text: `color-mix(in oklab, var(${token}) 62%, var(--popover-foreground))`,
  }) as const;

const success = tint('--success');
const warning = tint('--warning');
const error = tint('--destructive');
const info = tint('--info');

const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      theme="light"
      className="toaster group"
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--corner-radius)',

          '--success-bg': success.background,
          '--success-border': success.border,
          '--success-text': success.text,

          '--warning-bg': warning.background,
          '--warning-border': warning.border,
          '--warning-text': warning.text,

          '--error-bg': error.background,
          '--error-border': error.border,
          '--error-text': error.text,

          '--info-bg': info.background,
          '--info-border': info.border,
          '--info-text': info.text,
        } as React.CSSProperties
      }
      {...props}
    />
  );
};

export { Toaster };
